import axios from './axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { stringifyQueryString } from '~/utils/string';

export interface AxiosRequestClient {
    request(config: AxiosRequestConfig): Promise<AxiosResponse>;
}

export interface BaseClientConfig {
    apiBase?: string | null;
    client?: AxiosRequestClient;
}

export interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
    cancelKey?: string | symbol;
    cache?: {
        interpretHeader: boolean;
        cacheTakeover: boolean;
        methods: string[];
    };
}

class BaseClient {
    protected apiBase: string | null;
    protected client: AxiosRequestClient;
    protected cancelableRequests: Map<string | symbol, AbortController>;

    constructor(config: BaseClientConfig = {}) {
        this.apiBase = config?.apiBase || null;
        this.client = config?.client || axios;
        this.cancelableRequests = new Map();
    }

    async makeCoreApiRequest(
        url: string,
        method: string = 'get',
        data: object | null = null,
        params: object | null = null,
        extraConfig?: ExtendedAxiosRequestConfig
    ): Promise<AxiosResponse> {
        extraConfig = extraConfig || {};

        // Handle request cancellation when cancelKey is provided
        if (extraConfig.cancelKey) {
            this.cancelRequest(extraConfig.cancelKey);

            const abortController = new AbortController();
            this.cancelableRequests.set(extraConfig.cancelKey, abortController);

            extraConfig.signal = abortController.signal;
        }

        // Build request configuration
        const queryString = this.computeQueryString(params);
        const config: AxiosRequestConfig = {
            method: method.toLowerCase(),
            url: queryString ? `${url}?${queryString}` : url,
            data: data,
            ...extraConfig,
        };

        // Axios deep-merges a plain-object body into its config and drops own __proto__ and
        // constructor keys on the way, so a JSON body is serialised before Axios sees it.
        if (null !== data && !(data instanceof FormData)) {
            config.data = JSON.stringify(data);
            config.headers = { ...config.headers, 'Content-Type': 'application/json' };
        }

        return this.client.request(config);
    }

    cancelRequest(cancelKey: string | symbol): void {
        const controller = this.cancelableRequests.get(cancelKey);
        if (controller) {
            controller.abort();
            this.cancelableRequests.delete(cancelKey);
        }
    }

    computeQueryString(params?: object | null): string {
        return stringifyQueryString(params);
    }
}

export default BaseClient;
